'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Search, Sparkles, ChevronRight, ArrowRight, CheckCircle2 } from 'lucide-react';



const exampleSearches = [
  'Necesito una empresa que fabrique e instale una estructura metálica',
  'Busco proveedor de empaques biodegradables para e-commerce en Bogotá',
  'Necesito servicio de mantenimiento industrial para planta de producción',
  'Busco empresa que diseñe y construya local comercial en Medellín',
  'Necesito proveedor de insumos para restaurante en Cartagena',
  'Busco empresa de logística con cobertura nacional',
  'Necesito fabricante de muebles de oficina en Cali',
  'Busco servicio de fumigación para bodega industrial',
];

const recentSearches = [
  { text: 'Estructuras metálicas · Medellín' },
  { text: 'Servicio de mantenimiento · Bogotá' },
  { text: 'Empaques y packaging · Cali' },
  { text: 'Logística y transporte · Nacional' },
];

const businessBenefits = [
  'Una empresa puede vender, fabricar e instalar al mismo tiempo',
  'Perfil híbrido: productos + servicios + capacidades',
  'Aparece ante compradores que buscan lo que ofreces',
  'Registro completamente gratuito',
];

// ── Detect if query is conversational (should go to AI sourcing) ──
function isConversationalQuery(text: string): boolean {
  const t = text.toLowerCase().trim();
  if (!t) return false;
  const conversationalSignals = [
    'necesito', 'quiero', 'hola', 'estoy buscando', 'me gustaría',
    'quisiera', 'requiero', 'proyecto', 'fabricar', 'instalar', 'construir',
    'diseñar', 'desarrollar', 'hacer', 'que me haga', 'que pueda', 'que sepa',
    'con materiales', 'en mi casa', 'en mi empresa', 'para mi', 'para mi negocio',
    'alguien que', 'empresa que', 'proveedor que', 'quien me', 'quien pueda',
    'qué empresa', 'que empresa', 'qué proveedor', 'que proveedor',
    'quién vende', 'quien vende', 'quién fabrica', 'quien fabrica',
    'dónde consigo', 'donde consigo', 'cómo puedo', 'como puedo',
  ];
  const startsLikeQuestion = /^(qué|que|quién|quien|cómo|como|dónde|donde|cuál|cual|cuánto|cuanto|puede|pueden)\b/.test(t);
  return t.includes('?') || startsLikeQuestion || conversationalSignals.some(signal => t.includes(signal));
}

export default function HeroSection() {
  const [query, setQuery] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [exampleIdx, setExampleIdx] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [charIdx, setCharIdx] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [queryMode, setQueryMode] = useState<'normal' | 'ai'>('normal');
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Detect mode as user types
  useEffect(() => {
    if (query.trim().length > 0) {
      setQueryMode(isConversationalQuery(query) ? 'ai' : 'normal');
    } else {
      setQueryMode('normal');
    }
  }, [query]);

  useEffect(() => {
    if (query) return;
    const currentExample = exampleSearches[exampleIdx];
    if (isTyping) {
      if (charIdx < currentExample.length) {
        const t = setTimeout(() => {
          setPlaceholder(currentExample.slice(0, charIdx + 1));
          setCharIdx((c) => c + 1);
        }, 38);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setIsTyping(false), 2200);
        return () => clearTimeout(t);
      }
    } else {
      if (charIdx > 0) {
        const t = setTimeout(() => {
          setPlaceholder(currentExample.slice(0, charIdx - 1));
          setCharIdx((c) => c - 1);
        }, 18);
        return () => clearTimeout(t);
      } else {
        setExampleIdx((i) => (i + 1) % exampleSearches.length);
        setIsTyping(true);
      }
    }
  }, [charIdx, isTyping, exampleIdx, query]);

  const handleSearch = () => {
    if (!query.trim()) return;
    setIsSearching(true);
    setTimeout(() => {
      if (isConversationalQuery(query)) {
        window.location.href = `/buyer-dashboard?tab=ai-sourcing&q=${encodeURIComponent(query)}`;
      } else {
        window.location.href = `/search?q=${encodeURIComponent(query)}`;
      }
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
    if (e.key === 'Escape') setShowSuggestions(false);
  };

  const updateDropdownStyle = () => {
    if (searchBarRef.current) {
      const rect = searchBarRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  };

  useEffect(() => {
    if (showSuggestions) {
      updateDropdownStyle();
      window.addEventListener('resize', updateDropdownStyle);
      window.addEventListener('scroll', updateDropdownStyle, { passive: true });
    }
    return () => {
      window.removeEventListener('resize', updateDropdownStyle);
      window.removeEventListener('scroll', updateDropdownStyle);
    };
  }, [showSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchBarRef.current && !searchBarRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section id="main-content" className="relative min-h-screen flex flex-col pt-14 bg-black overflow-hidden">
      {/* ── VIDEO BACKGROUND — Desktop & Tablet (hidden on mobile) ── */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="hidden sm:block absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ zIndex: 0 }}
        aria-hidden="true"
      >
        <source
          src="https://hdpxwuqbabwfepiecdsz.supabase.co/storage/v1/object/public/video/Moondepth_rough_cut_v2_sin_brincos.mp4"
          type="video/mp4"
        />
      </video>

      {/* ── VIDEO BACKGROUND — Mobile only ── */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="block sm:hidden absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ zIndex: 0 }}
        aria-hidden="true"
      >
        <source
          src="https://hdpxwuqbabwfepiecdsz.supabase.co/storage/v1/object/public/video/hf_20260906_000006_53d75994-c406-4249-9a68-7f9a4f46483b.mp4"
          type="video/mp4"
        />
      </video>

      {/* Dark overlay to keep content readable */}
      <div className="absolute inset-0 bg-black/55 pointer-events-none" style={{ zIndex: 1 }} aria-hidden="true" />

      {/* Subtle dot grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
          zIndex: 2,
        }}
        aria-hidden="true"
      />

      {/* ── CENTERED LOGO ── */}
      <div
        className="relative flex flex-col items-center justify-center pt-10 pb-2"
        style={{ zIndex: 10, mixBlendMode: 'screen' }}
      >
        <img
          src="/assets/images/MOONDEPTH-logo-fondo-negro-lunas-originales-1787438093921.png"
          alt="MoonDepth — Motor B2B de abastecimiento inteligente"
          width={340}
          height={340}
          style={{
            display: 'block',
            objectFit: 'contain',
          }}
        />
      </div>

      {/* ── DUAL PANEL LAYOUT ── */}
      <div className="relative flex-1 flex flex-col lg:flex-row" style={{ zIndex: 10 }}>

        {/* ── LEFT HALF: Search ── */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 lg:px-14 xl:px-20 2xl:px-28 py-8 lg:py-0 lg:border-r lg:border-white/10 relative">
          <div className="w-full max-w-xl">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 mb-8 animate-slide-up"
              style={{ animationDelay: '0ms' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" aria-hidden="true" />
              <span className="text-xs font-medium text-white/60">
                Motor B2B de abastecimiento inteligente
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-black text-white mb-6 animate-slide-up"
              style={{
                fontSize: 'clamp(2.5rem, 4.4vw, 4.25rem)',
                lineHeight: '1.02',
                letterSpacing: '-0.04em',
                animationDelay: '60ms',
              }}
            >
              <span className="sm:whitespace-nowrap">Encuentra proveedores</span>
              <br />
              con IA.
            </h1>

            <p
              className="text-white/50 text-base leading-relaxed mb-10 max-w-md animate-slide-up"
              style={{ animationDelay: '120ms' }}
            >
              Describe lo que necesitas en lenguaje natural. MoonDepth conecta tu empresa con los proveedores correctos.
            </p>

            {/* Search bar */}
            <div
              className="relative animate-slide-up"
              style={{ animationDelay: '180ms' }}
              ref={searchBarRef}
            >
              <div
                className={`flex items-center gap-3 px-4 py-1.5 rounded-xl border transition-all duration-200 ${
                  queryMode === 'ai' ?'border-white/40 bg-white/10 shadow-[0_0_0_3px_rgba(255,255,255,0.06)]' :'border-white/20 bg-white/5 focus-within:border-white/40 focus-within:bg-white/8'
                }`}
              >
                {queryMode === 'ai' ? (
                  <Sparkles size={18} className="text-white/60 flex-shrink-0" aria-hidden="true" />
                ) : (
                  <Search size={18} className="text-white/40 flex-shrink-0" aria-hidden="true" />
                )}
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowSuggestions(e.target.value.length === 0);
                  }}
                  onFocus={() => { if (!query) setShowSuggestions(true); }}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder || 'Busca un producto, servicio o empresa...'}
                  className="flex-1 bg-transparent text-white text-sm placeholder-white/30 outline-none min-w-0"
                  aria-label="Buscar proveedores"
                  autoComplete="off"
                />
                {query && (
                  <button
                    onClick={() => { setQuery(''); setQueryMode('normal'); }}
                    className="text-white/30 hover:text-white/60 transition-colors p-1 rounded"
                    aria-label="Limpiar búsqueda"
                  >
                    ×
                  </button>
                )}
                <button
                  onClick={handleSearch}
                  disabled={!query.trim() || isSearching}
                  className="flex items-center gap-2 px-4 py-1 bg-white text-black text-sm font-semibold rounded-lg hover:bg-white/90 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  aria-label="Buscar"
                >
                  {isSearching ? (
                    <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" aria-hidden="true" />
                  ) : queryMode === 'ai' ? (
                    <>
                      <Sparkles size={14} aria-hidden="true" />
                      <span className="hidden sm:inline">AI Sourcing</span>
                    </>
                  ) : (
                    <>
                      <Search size={14} aria-hidden="true" />
                      <span className="hidden sm:inline">Buscar</span>
                    </>
                  )}
                </button>
              </div>

              {/* Suggestions dropdown */}
              {showSuggestions && (
                <div
                  style={dropdownStyle}
                  className="rounded-2xl shadow-2xl overflow-hidden"
                  style={{ ...dropdownStyle, background: '#0d0d12', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 8px 40px rgba(0,0,0,0.8)' }}
                >
                  <div className="px-4 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Búsquedas recientes</p>
                  </div>
                  {recentSearches.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setQuery(s.text);
                        setShowSuggestions(false);
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-white/[0.04]"
                    >
                      <div className="flex items-center gap-3">
                        <Search size={13} style={{ color: 'rgba(255,255,255,0.35)' }} className="flex-shrink-0" aria-hidden="true" />
                        <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{s.text}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Arma tu proyecto con IA */}
            <div
              className="mt-5 animate-slide-up"
              style={{ animationDelay: '240ms' }}
            >
              <Link
                href="/buyer-dashboard?tab=ai-sourcing"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-xs font-medium text-white/60 hover:text-white hover:border-white/40 transition-all duration-150"
              >
                <Sparkles size={12} aria-hidden="true" />
                Arma tu proyecto con IA
                <ChevronRight size={11} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>

        {/* ── RIGHT HALF: Register your business ── */}
        <div className="w-full lg:w-[42%] xl:w-[40%] flex flex-col items-center justify-center px-6 lg:px-10 xl:px-14 py-8 lg:py-0">
          <div className="w-full max-w-sm">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" aria-hidden="true" />
              <span className="text-xs font-medium text-white/60">
                Registro gratuito activo
              </span>
            </div>

            <h2
              className="font-black text-white mb-4"
              style={{
                fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
                lineHeight: '1.05',
                letterSpacing: '-0.03em',
              }}
            >
              Registra tu
              <br />
              comercio o
              <br />
              servicio.
            </h2>

            <p className="text-white/50 text-sm leading-relaxed mb-8">
              Productos, servicios, fabricación y soluciones empresariales en una sola red inteligente.
              Una empresa. Un perfil. Múltiples capacidades.
            </p>

            <div className="flex flex-col gap-3 mb-8">
              {businessBenefits.map((benefit) => (
                <div key={`benefit-${benefit.slice(0, 20)}`} className="flex items-start gap-3">
                  <CheckCircle2 size={15} className="text-white/60 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-sm text-white/70 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>

            <Link
              href="/sign-up-login"
              className="flex items-center justify-center gap-3 w-full px-8 py-4 bg-white text-black text-sm font-bold rounded-2xl hover:bg-white/90 transition-all duration-150 active:scale-95 mb-4"
            >
              Registra tu comercio o servicio
              <ArrowRight size={16} aria-hidden="true" />
            </Link>

            <p className="text-center text-xs text-white/30">
              Gratis durante 5 meses · Sin tarjeta de crédito
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
